import type { ComponentType } from 'react'
import type { SvgIconProps } from '@mui/material'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded'
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined'
import TuneRoundedIcon from '@mui/icons-material/TuneRounded'
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined'
import AccountTreeRoundedIcon from '@mui/icons-material/AccountTreeRounded'
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined'
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded'
import type { HelperChecklistComment } from '../../services/helper/helperChecklistDetailsService'
import type { BlockerCtaSection } from '../../types/checklistBlockerCodes'

export const shouldPromptDiscard = (hasManualSaveDraft: boolean, isPeerLocked: boolean): boolean => {
  return hasManualSaveDraft && !isPeerLocked
}

export const getCurrencyAffix = (currencyCode?: string | null): string => {
  const code = (currencyCode || 'USD').trim().toUpperCase()
  switch (code) {
    case 'USD':
      return '$'
    case 'EUR':
      return '€'
    case 'GBP':
      return '£'
    case 'INR':
      return '₹'
    default:
      return code
  }
}

export const formatCommentRelativeTime = (value: unknown): string => {
  const ms =
    typeof value === 'number'
      ? value
      : typeof value === 'string'
        ? Date.parse(value)
        : typeof value === 'object' && value && 'toDate' in (value as Record<string, unknown>)
          ? (value as { toDate: () => Date }).toDate().getTime()
          : NaN
  if (Number.isNaN(ms)) return ''
  const delta = Date.now() - ms
  if (delta < 60_000) return 'Just now'
  if (delta < 3_600_000) return `${Math.floor(delta / 60_000)}m ago`
  if (delta < 86_400_000) return `${Math.floor(delta / 3_600_000)}h ago`
  if (delta < 7 * 86_400_000) return `${Math.floor(delta / 86_400_000)}d ago`
  return new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

export type DetailTab = 'overview' | 'details' | 'assignment' | 'proof'

export interface DisplayChecklistComment extends HelperChecklistComment {
  isPending?: boolean
  isFailed?: boolean
  error?: string
  mutationId?: string
}

export interface MemberDetailTabDef {
  key: DetailTab
  label: string
  icon: ComponentType<SvgIconProps>
  iconActive: ComponentType<SvgIconProps>
}

export const DETAIL_TABS: MemberDetailTabDef[] = [
  {
    key: 'overview',
    label: 'Overview',
    icon: DescriptionOutlinedIcon,
    iconActive: DescriptionRoundedIcon,
  },
  {
    key: 'details',
    label: 'Details',
    icon: TuneOutlinedIcon,
    iconActive: TuneRoundedIcon,
  },
  {
    key: 'assignment',
    label: 'Assignment',
    icon: AccountTreeOutlinedIcon,
    iconActive: AccountTreeRoundedIcon,
  },
  {
    key: 'proof',
    label: 'Proof',
    icon: VerifiedOutlinedIcon,
    iconActive: VerifiedRoundedIcon,
  },
]

export const SECTION_TO_TAB: Record<BlockerCtaSection, DetailTab> = {
  description: 'overview',
  completionCriteria: 'overview',
  signals: 'details',
  dates: 'details',
  resources: 'details',
  assignee: 'assignment',
  dependencies: 'assignment',
  fieldPass: 'assignment',
  attachedForms: 'assignment',
  proof: 'proof',
  approver: 'proof',
  location: 'details',
}
